/**
 * RaiseDoubtModal — task owner raises a doubt for Admin to respond to.
 *
 * Per BIT 9:
 *   - Only the task owner can raise.
 *   - Goes to Admin (SK/GM) only — visible only to Admin/Super in inbox.
 *   - Not visible to other team members.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../../api/supabase.js';
import { useAuth } from '../../context/AuthContext.jsx';
import Modal from '../ui/Modal.jsx';

export default function RaiseDoubtModal({ open, onClose, taskId, onRaised }) {
  const { profile } = useAuth();
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setQuestion('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = question.trim();
    if (!trimmed) {
      setError('Please describe your doubt.');
      return;
    }
    setSubmitting(true);
    const { error: insertError } = await supabase.from('doubts').insert({
      task_id: taskId,
      raised_by: profile.id,
      question: trimmed,
      status: 'open',
    });
    setSubmitting(false);
    if (insertError) {
      console.error('[ORIN] Raise doubt failed:', insertError);
      setError(insertError.message || 'Failed to raise doubt. Try again.');
      return;
    }
    onRaised?.();
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Raise a doubt"
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn" disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="raise-doubt-form"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send to Admin'}
          </button>
        </>
      }
    >
      <form
        id="raise-doubt-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
        noValidate
      >
        <p className="text-sm text-muted">
          This doubt will be sent to the Admin team. Your teammates won&apos;t see it.
        </p>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={5}
          placeholder="What are you unsure about?"
          required
          disabled={submitting}
          className="w-full px-3 py-2 rounded-md border border-border-base bg-card text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2"
        />
        {error && (
          <div
            className="text-sm text-status-overdue bg-red-50 border border-red-200 rounded-md px-3 py-2"
            role="alert"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}